import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useProduct } from '../../context/ProductContext'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { MoneyInput } from '../../components/ui/MoneyInput'
import { Button } from '../../components/ui/Button'
import * as categoryService from '../../services/categoryService'
import { ImageUploadField } from '../../components/ui/ImageUploadField'
import { ProductImageGalleryEditor } from '../../components/ui/ProductImageGalleryEditor'
import { CategorySearchSelect } from '../../components/ui/CategorySearchSelect'
import { normalizeProductImageUrls } from '../../utils/productImages'
import { SkeletonFormSections } from '../../components/ui/Skeleton'

const fieldAreaClass =
  'w-full rounded-2xl border border-border bg-card px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 min-h-[96px]'

function numPrice(p: unknown) {
  if (p == null) return ''
  const n = Number(p)
  return Number.isFinite(n) ? String(n) : String(p)
}

export default function ProductEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { selectedProduct, getProductById, updateProduct, mutationLoading, error, clearError } =
    useProduct()
  const [categories, setCategories] = useState<categoryService.CategoryRow[]>([])
  const [categoryFieldError, setCategoryFieldError] = useState('')
  const [imageUploading, setImageUploading] = useState(false)
  const [form, setForm] = useState({
    name: '',
    sku: '',
    price: '',
    stockQuantity: '0',
    description: '',
    otherDetails: '',
    categorySlug: '',
    isActive: true,
    imageUrls: [] as string[],
  })

  useEffect(() => {
    categoryService
      .fetchCategories({ page: 1, limit: 100 })
      .then((d) => setCategories(d.items || []))
      .catch(() => setCategories([]))
  }, [])

  const assignableCategories = useMemo(
    () => categories.filter((c) => c.id != null && c.source === 'store'),
    [categories],
  )

  const storedCategoryInvalid =
    Boolean(form.categorySlug.trim()) &&
    !assignableCategories.some((c) => c.slug === form.categorySlug.trim())

  useEffect(() => {
    if (!id) return
    clearError()
    getProductById(id)
      .then((item) => {
        const imgs = normalizeProductImageUrls(item.images)
        setForm({
          name: item.name || '',
          sku: item.sku || '',
          price: numPrice(item.price),
          stockQuantity: String(item.stockQuantity ?? 0),
          description: item.description ?? '',
          otherDetails: item.otherDetails != null ? String(item.otherDetails) : '',
          categorySlug: item.categorySlug ?? '',
          isActive: item.isActive !== false,
          imageUrls: imgs,
        })
      })
      .catch(() => {})
  }, [id, getProductById, clearError])

  function removeImage(url: string) {
    setForm((f) => ({ ...f, imageUrls: f.imageUrls.filter((u) => u !== url) }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!id) return
    clearError()
    const cat = form.categorySlug.trim()
    if (cat && !assignableCategories.some((c) => c.slug === cat)) {
      setCategoryFieldError('Select a category from the list or “No category” before saving.')
      return
    }
    setCategoryFieldError('')
    await updateProduct(id, {
      name: form.name.trim(),
      ...(form.sku.trim() ? { sku: form.sku.trim() } : {}),
      price: Number(form.price),
      stockQuantity: Number(form.stockQuantity),
      description: form.description.trim() || undefined,
      otherDetails: form.otherDetails.trim() ? form.otherDetails.trim() : null,
      isActive: form.isActive,
      categorySlug: cat || undefined,
      images: form.imageUrls,
    })
    navigate(`/products/${id}`)
  }

  return (
    <Card className="w-full max-w-none">
      <CardHeader
        title="Edit product"
        description="Store assignment stays on your primary store unless changed via API. Product URL slug is set at creation and is not editable here."
      />
      {!selectedProduct ? (
        <SkeletonFormSections />
      ) : (
        <form onSubmit={handleSubmit} className="w-full min-w-0 space-y-6 lg:space-y-8">
          {/* Basics: name, SKU, slug, category */}
          <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-5">
            <Input
              id="name"
              label="Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="min-w-0"
            />
            <Input
              id="sku"
              label="SKU"
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              placeholder="Optional — leave unchanged if blank"
              className="min-w-0"
            />
            {selectedProduct?.slug ? (
              <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3 text-sm lg:col-span-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Storefront URL slug</p>
                <p className="mt-1 font-mono text-foreground break-all">{selectedProduct.slug}</p>
                <p className="mt-2 text-xs text-muted-foreground">Generated by the system and stable for your product links.</p>
              </div>
            ) : null}
            <div className="min-w-0 lg:col-span-2">
              <label htmlFor="categorySlug" className="mb-2 block text-sm font-medium text-foreground">
                Category
              </label>
              <CategorySearchSelect
                id="categorySlug"
                value={
                  storedCategoryInvalid
                    ? ''
                    : assignableCategories.some((c) => c.slug === form.categorySlug)
                      ? form.categorySlug
                      : ''
                }
                onValueChange={(slug) => {
                  setCategoryFieldError('')
                  setForm({ ...form, categorySlug: slug })
                }}
                categories={assignableCategories}
                aria-invalid={storedCategoryInvalid}
              />
              {storedCategoryInvalid ? (
                <p className="mt-2 rounded-xl border border-[color-mix(in_oklch,var(--warning)_35%,transparent)] bg-[color-mix(in_oklch,var(--warning)_12%,transparent)] px-3 py-2 text-xs text-foreground">
                  Stored slug “{form.categorySlug.trim()}” is not a category preset. Select a category below or “No
                  category” to clear, then save.
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  Only categories you created under Categories can be assigned.
                </p>
              )}
              {categoryFieldError ? <p className="mt-2 text-sm text-destructive">{categoryFieldError}</p> : null}
            </div>
          </div>

          {/* Pricing & stock */}
          <div className="grid grid-cols-1 gap-4 sm:max-w-2xl sm:gap-5 lg:max-w-none lg:grid-cols-2 lg:gap-x-8">
            <MoneyInput
              id="price"
              label="Price *"
              value={form.price}
              onChange={(price) => setForm({ ...form, price })}
              required
              className="min-w-0"
            />
            <Input
              id="stockQuantity"
              label="Stock quantity *"
              type="number"
              min="0"
              value={form.stockQuantity}
              onChange={(e) => setForm({ ...form, stockQuantity: e.target.value })}
              required
              className="min-w-0"
            />
          </div>

          <label className="flex w-fit items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Published on storefront
          </label>

          {/* Description, other details | Images */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-5 xl:gap-8">
            <div className="min-w-0 xl:col-span-3 space-y-5">
              <div>
                <label htmlFor="description" className="mb-2 block text-sm font-medium text-foreground">
                  Description
                </label>
                <textarea
                  id="description"
                  className={fieldAreaClass}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={6}
                />
              </div>
              <div>
                <label htmlFor="otherDetails" className="mb-2 block text-sm font-medium text-foreground">
                  Other details <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <p className="mb-2 text-xs text-muted-foreground">
                  Appears on the storefront under &quot;Other details&quot; when provided.
                </p>
                <textarea
                  id="otherDetails"
                  className={fieldAreaClass}
                  value={form.otherDetails}
                  onChange={(e) => setForm({ ...form, otherDetails: e.target.value })}
                  rows={5}
                  placeholder="Specs, care, ingredients, dimensions…"
                />
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-5 xl:col-span-2">
              <span className="text-sm font-medium text-foreground">Images</span>
              <ProductImageGalleryEditor
                urls={form.imageUrls}
                onRemove={removeImage}
                caption="Current images — remove any you no longer need. New uploads are added below."
              />
              <ImageUploadField
                label={form.imageUrls.length ? 'Add more images' : 'Upload images'}
                hint="PNG, JPEG, WebP, or GIF. New files are appended to the gallery in order."
                multiple
                disabled={mutationLoading}
                onUploadStateChange={setImageUploading}
                onUploadError={(msg) => window.alert(msg)}
                onUploaded={(urls) => {
                  clearError()
                  setForm((f) => ({ ...f, imageUrls: [...f.imageUrls, ...urls] }))
                }}
              />
            </div>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-end">
            <Button type="submit" disabled={mutationLoading || imageUploading} className="w-full sm:w-auto sm:min-w-[10rem]">
              {mutationLoading ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  )
}
